import React, { useState, useEffect, useRef, useCallback } from 'react';
import './BrowsePhotographer.css';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, orderBy, where, limit, startAfter,  addDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import Modal from './modal';
import logo from './assets/logo.png';
// Local Storage Helper
const LocalStorageHelper = {
  saveLoginData: (userId, email, name, userType = 'user') => {
    localStorage.setItem('isLoggedIn', 'true');
    localStorage.setItem('userId', userId);
    localStorage.setItem('userEmail', email);
    localStorage.setItem('userName', name);
    localStorage.setItem('userType', userType);
  },
  isLoggedIn: () => {
    return localStorage.getItem('isLoggedIn') === 'true';
  },
  getLoginData: () => {
    return {
      userId: localStorage.getItem('userId'),
      email: localStorage.getItem('userEmail'),
      name: localStorage.getItem('userName'),
      userType: localStorage.getItem('userType'),
    };
  },
  clearLoginData: () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');
    localStorage.removeItem('userType');
  }
};

// Helper function to calculate accurate rating from reviews
const calculateAverageRating = (reviews) => {
  if (!reviews || reviews.length === 0) return 0;
  
  const totalRating = reviews.reduce((sum, review) => {
    return sum + (review.rating || 0);
  }, 0);
  
  return parseFloat((totalRating / reviews.length).toFixed(1));
};

// Enhanced helper to load reviews for accurate rating
const loadPhotographerReviews = async (photographerId) => {
  try {
    const reviewsQuery = query(
      collection(db, 'reviews', photographerId, 'reviews'),
      orderBy('createdAt', 'desc')
    );

    const reviewsSnapshot = await getDocs(reviewsQuery);
    const reviewsData = [];

    reviewsSnapshot.forEach((doc) => {
      const reviewData = doc.data();
      reviewsData.push({
        id: doc.id,
        ...reviewData,
        createdAt: reviewData.createdAt?.toDate() || new Date()
      });
    });

    return reviewsData;
  } catch (error) {
    console.error('Error loading reviews for photographer:', photographerId, error);
    return [];
  }
};

const BrowsePhotographer = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    budget: '',
    services: '',
    rating: '',
    location: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(9);

  // Firebase state
  const [allPhotographers, setAllPhotographers] = useState([]);
  const [filteredPhotographers, setFilteredPhotographers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastDocument, setLastDocument] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // 🔴 NEW: Dynamic filter options
  const [availableServices, setAvailableServices] = useState([]);
  const [budgetRanges, setBudgetRanges] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);

  // Authentication state
  const [logged_in, setLogged_in] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const profileWidgetRef = useRef(null);

  // Mobile filter state
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Mobile sidebar state
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isNavMenuOpen, setIsNavMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Check if screen size is mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Mobile sidebar functions
  const toggleMobileSidebar = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
    if (!isMobileSidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const closeMobileSidebar = () => {
    setIsMobileSidebarOpen(false);
    document.body.style.overflow = 'unset';
  };

  const handleMobileNavClick = (path) => {
    navigate(path);
    closeMobileSidebar();
  };

  // Mobile filter functions
  const toggleMobileFilters = () => {
    setShowMobileFilters(!showMobileFilters);
    if (!showMobileFilters) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const closeMobileFilters = () => {
    setShowMobileFilters(false);
    document.body.style.overflow = 'unset';
  };

  const handleMobileFilterApply = () => {
    closeMobileFilters();
  };

  const handleMobileFilterClear = () => {
    clearAllFilters();
    closeMobileFilters();
  };

  // Add useEffect to handle escape key and cleanup
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        if (showMobileFilters) {
          closeMobileFilters();
        }
        if (isMobileSidebarOpen) {
          closeMobileSidebar();
        }
      }
    };

    if (showMobileFilters || isMobileSidebarOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
        document.body.style.overflow = 'unset';
      };
    }
  }, [showMobileFilters, isMobileSidebarOpen]);

  // Firebase Authentication Check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthLoading(true);
      if (user) {
        try {
          // Get user type from localStorage first
          const loginData = LocalStorageHelper.getLoginData();
          let userType = loginData.userType || 'user';
          console.log('Fetching user data for:', user.uid, 'userType:', userType);

          // Try to get user data from the expected collection first
          let userDocRef = doc(db, userType === 'vendor' || userType === 'photographer' ? 'photographers' : 'users', user.uid);
          let userDoc = await getDoc(userDocRef);

          // If not found in expected collection, try the other collection
          if (!userDoc.exists()) {
            console.log('User not found in expected collection, trying alternative...');
            userType = userType === 'vendor' || userType === 'photographer' ? 'user' : 'photographer';
            userDocRef = doc(db, userType === 'photographer' ? 'photographers' : 'users', user.uid);
            userDoc = await getDoc(userDocRef);

            // Update localStorage with correct user type
            if (userDoc.exists()) {
              LocalStorageHelper.saveLoginData(
                user.uid,
                user.email,
                user.displayName || "User",
                userType
              );
            }
          }

          if (userDoc.exists()) {
            const userData = userDoc.data();

            // Generate avatar URL
            const getAvatarUrl = (userData, user) => {
              const avatarSources = [
                userData.profileImage,
                userData.avatarUrl,
                userData.avatar,
                user.photoURL
              ];
              const validAvatar = avatarSources.find(url => url && url.trim() !== '' && url !== 'undefined');
              if (validAvatar) {
                return validAvatar;
              }
              const name = userData.name || user.displayName || "User";
              return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
            };

            // Set user profile data
            const profileData = {
              id: user.uid,
              name: userData.name || user.displayName || "User",
              email: userData.email || user.email,
              avatar: getAvatarUrl(userData, user),
              role: userType,
              joinDate: userData.createdAt,
              isVerified: user.emailVerified || userData.emailVerified || false,
              userType: userType
            };

            console.log('Setting user profile:', profileData);
            setUserProfile(profileData);
            setLogged_in(true);

            // Update localStorage to ensure consistency
            LocalStorageHelper.saveLoginData(
              user.uid,
              userData.email || user.email,
              userData.name || user.displayName || "User",
              userType
            );
          } else {
            console.log('User document not found in either collection');
            // Create basic profile data from auth user
            const basicProfile = {
              id: user.uid,
              name: user.displayName || "User",
              email: user.email,
              userType: 'user',
              avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'User')}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`,
              isVerified: user.emailVerified || false
            };
            setUserProfile(basicProfile);
            setLogged_in(true);
            LocalStorageHelper.saveLoginData(
              user.uid,
              user.email,
              user.displayName || "User",
              'user'
            );
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          // Still set basic profile from auth user if Firestore fails
          const basicProfile = {
            id: user.uid,
            name: user.displayName || "User",
            email: user.email,
            userType: 'user',
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || 'User')}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`,
            isVerified: user.emailVerified || false
          };
          setUserProfile(basicProfile);
          setLogged_in(true);
        }
      } else {
        // User is not logged in
        console.log('No user logged in');
        setLogged_in(false);
        setUserProfile(null);
        LocalStorageHelper.clearLoginData();
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Enhanced Logout Handler
  const handleLogout = useCallback(async () => {
    try {
      console.log('Logging out user');
      // Sign out from Firebase
      await signOut(auth);
      // Clear local storage
      LocalStorageHelper.clearLoginData();
      // Reset state
      setLogged_in(false);
      setUserProfile(null);
      setShowProfileMenu(false);
      closeMobileSidebar();
      console.log('User logged out successfully');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  }, []);

  // Profile Menu Handlers
  const toggleProfileMenu = () => {
    console.log('Profile widget clicked, current state:', showProfileMenu);
    setShowProfileMenu(prev => !prev);
  };

  // Enhanced Profile Click Handler - Updated for photographers
  const handleProfileClick = () => {
    console.log("=== Profile Settings Clicked ===");
    console.log("Profile clicked, navigating based on user type");
    console.log("User data:", userProfile);
    setShowProfileMenu(false);
    closeMobileSidebar();

    // Always navigate to Dashboard for photographers
    if (userProfile?.userType === 'vendor' || userProfile?.userType === 'photographer') {
      navigate('/Upload');
    } else {
      navigate('/ProfileSettings');
    }
  };

  // Settings handler - Only for non-photographers
  const handleSettingsClick = () => {
    console.log("=== Settings Clicked ===");
    setShowProfileMenu(false);
    closeMobileSidebar();
    navigate('/ProfileSettings');
  };

  const handleMyBookingsClick = () => {
    console.log("=== My Bookings Clicked ===");
    setShowProfileMenu(false);
    closeMobileSidebar();
    navigate('/UserBookings');
  };

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileWidgetRef.current && !profileWidgetRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    if (showProfileMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProfileMenu]);

  // 🔴 NEW: Generate dynamic filter options based on loaded data
  const generateFilterOptions = (photographersData) => {
    const services = new Set();
    const locations = new Set();
    const prices = [];

    photographersData.forEach(photographer => {
      // Extract services from serviceCategories
      if (photographer.serviceCategories) {
        Object.keys(photographer.serviceCategories).forEach(service => {
          services.add(service);
        });
      }

      // Extract locations
      if (photographer.location) {
        locations.add(photographer.location);
      }

      // Extract prices for budget calculation
      if (photographer.serviceCategories) {
        Object.values(photographer.serviceCategories).forEach(price => {
          const numericPrice = parseFloat(price);
          if (!isNaN(numericPrice) && numericPrice > 0) {
            prices.push(numericPrice);
          }
        });
      }
    });

    // Generate dynamic budget ranges based on actual prices
    const generateBudgetRanges = (prices) => {
      if (prices.length === 0) return [];

      const sortedPrices = prices.sort((a, b) => a - b);
      const minPrice = Math.floor(sortedPrices[0] / 1000) * 1000;
      const maxPrice = Math.ceil(sortedPrices[sortedPrices.length - 1] / 1000) * 1000;
      
      const ranges = [];
      const step = Math.ceil((maxPrice - minPrice) / 4 / 1000) * 1000; // Create 4-5 ranges

      for (let i = minPrice; i < maxPrice; i += step) {
        const rangeStart = i;
        const rangeEnd = i + step;
        ranges.push({
          value: `${rangeStart}-${rangeEnd}`,
          label: `₹${rangeStart.toLocaleString('en-IN')} - ₹${rangeEnd.toLocaleString('en-IN')}`
        });
      }

      // Add "Above" range for highest prices
      if (ranges.length > 0) {
        const lastRange = ranges[ranges.length - 1];
        const lastRangeEnd = parseInt(lastRange.value.split('-')[1]);
        ranges.push({
          value: `${lastRangeEnd}+`,
          label: `₹${lastRangeEnd.toLocaleString('en-IN')}+`
        });
      }

      return ranges;
    };

    setAvailableServices(Array.from(services).sort());
    setAvailableLocations(Array.from(locations).sort());
    setBudgetRanges(generateBudgetRanges(prices));
  };

  // 🔴 UPDATED: Enhanced filtering based on selected service and corresponding prices
  const getFilteredPricesForService = (photographer, selectedService) => {
    if (!selectedService || !photographer.serviceCategories) {
      // If no service selected, get all prices
      return Object.values(photographer.serviceCategories).map(price => parseFloat(price)).filter(p => !isNaN(p) && p > 0);
    }

    // If service selected, get price only for that service
    const servicePrice = photographer.serviceCategories[selectedService];
    const numericPrice = parseFloat(servicePrice);
    return !isNaN(numericPrice) && numericPrice > 0 ? [numericPrice] : [];
  };

  // 🔴 UPDATED: Load photographers from Firebase with dynamic filter generation
  const loadPhotographers = async (isLoadMore = false) => {
    try {
      setIsLoading(true);
      
      // Fetch all photographers without profileComplete filter in Firestore
      let photographersQuery = query(
        collection(db, 'photographers'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      if (isLoadMore && lastDocument) {
        photographersQuery = query(
          collection(db, 'photographers'),
          orderBy('createdAt', 'desc'),
          startAfter(lastDocument),
          limit(50)
        );
      }

      const snapshot = await getDocs(photographersQuery);

      // Only photographers with a complete profile are ever displayed, so
      // filter for that BEFORE fetching reviews — no point paying for a
      // reviews-subcollection read (and its own N Firestore document reads)
      // for a photographer who's about to be filtered out anyway.
      const completeProfileDocs = snapshot.docs.filter(
        (docSnapshot) => (docSnapshot.data().profileComplete || 0) === 100
      );

      const reviewsByPhotographer = await Promise.all(
        completeProfileDocs.map((docSnapshot) => loadPhotographerReviews(docSnapshot.id))
      );

      const completeProfilePhotographers = completeProfileDocs.map((docSnapshot, index) => {
        const data = docSnapshot.data();
        const photographerId = docSnapshot.id;

        const reviews = reviewsByPhotographer[index];
        const accurateRating = calculateAverageRating(reviews);

        const lowestPriceData = getLowestPriceWithLabel(data.serviceCategories || {});

        return {
          id: photographerId,
          name: data.name || 'Unknown Photographer',
          // No hardcoded fallback: an unreviewed photographer must not display
          // an invented score. 0 means "no reviews yet" and the card omits the
          // rating row entirely rather than showing a number nobody earned.
          rating: accurateRating || data.rating || 0,
          location: (data.serviceAreas && data.serviceAreas.length > 0) ? data.serviceAreas[0] : (data.address || data.city || 'Location not specified'),
          price: lowestPriceData.price,
          priceLabel: lowestPriceData.label,
          services: lowestPriceData.label || getMainService(data.serviceCategories || {}),
          featured: data.featured || false,
          imageUrl: data.profileImage || 'https://images.pexels.com/photos/1264210/pexels-photo-1264210.jpeg',
          description: data.description || '',
          phoneNumber: data.phone || '',
          email: data.email || '',
          portfolioImages: data.portfolioImages || [],
          specialties: data.specialties || [],
          yearsExperience: data.yearsExperience || 1,
          isVerified: data.isVerified || false,
          isOnline: data.isOnline || false,
          responseTime: data.responseTime || 'Quick response',
          reviewsCount: reviews.length,
          hasStudio: data.hasStudio || false,
          studioName: data.studioName || '',
          studioAddress: data.studioAddress || '',
          serviceCategories: data.serviceCategories || {},
          profileComplete: data.profileComplete || 0,
          reviews: reviews
        };
      });

      console.log(`Fetched ${snapshot.docs.length} photographers, ${completeProfilePhotographers.length} have complete profiles`);

      if (isLoadMore) {
        const newPhotographers = [...allPhotographers, ...completeProfilePhotographers];
        setAllPhotographers(newPhotographers);
        // 🔴 Update filter options with new data
        generateFilterOptions(newPhotographers);
      } else {
        setAllPhotographers(completeProfilePhotographers);
        // 🔴 Generate initial filter options
        generateFilterOptions(completeProfilePhotographers);
      }

      if (snapshot.docs.length > 0) {
        setLastDocument(snapshot.docs[snapshot.docs.length - 1]);
      }

      setHasMore(snapshot.docs.length === 50);
    } catch (error) {
      console.error('Error loading photographers:', error);
      toast.error('Error loading photographers. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Updated function to get lowest price WITH corresponding label
  const getLowestPriceWithLabel = (serviceCategories) => {
    if (!serviceCategories || Object.keys(serviceCategories).length === 0) {
      return { price: '5,000', label: 'Photography' };
    }

    let lowestPrice = Infinity;
    let correspondingLabel = 'Photography';

    // Iterate through all service categories to find the lowest price and its label
    Object.entries(serviceCategories).forEach(([categoryName, price]) => {
      const numericPrice = parseFloat(price);
      // Check if price is valid and lower than current lowest
      if (!isNaN(numericPrice) && numericPrice > 0 && numericPrice < lowestPrice) {
        lowestPrice = numericPrice;
        correspondingLabel = categoryName;
      }
    });

    // If no valid price found, return default
    if (lowestPrice === Infinity) {
      return { price: '5,000', label: 'Photography' };
    }

    return {
      price: lowestPrice.toLocaleString('en-IN'),
      label: correspondingLabel
    };
  };

  // Fallback function for main service (if needed)
  const getMainService = (serviceCategories) => {
    if (!serviceCategories || Object.keys(serviceCategories).length === 0) return 'Photography';
    const serviceKeys = Object.keys(serviceCategories);
    // Return the first available service category
    if (serviceKeys.length > 0) {
      return serviceKeys[0];
    }
    return 'Photography';
  };

  useEffect(() => {
    loadPhotographers();
  }, []);

  // 🔴 UPDATED: Enhanced filtering logic with service-specific budget filtering
  useEffect(() => {
    let filtered = allPhotographers;

    if (searchTerm) {
      filtered = filtered.filter(photographer =>
        photographer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        photographer.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        photographer.services.toLowerCase().includes(searchTerm.toLowerCase()) ||
        photographer.priceLabel.toLowerCase().includes(searchTerm.toLowerCase()) ||
        photographer.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        Object.keys(photographer.serviceCategories).some(service =>
          service.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // 🔴 UPDATED: Service filtering based on serviceCategories
    if (filters.services) {
      filtered = filtered.filter(photographer =>
        photographer.serviceCategories &&
        Object.keys(photographer.serviceCategories).some(service =>
          service.toLowerCase().includes(filters.services.toLowerCase())
        )
      );
    }

    // 🔴 UPDATED: Budget filtering based on selected service
    if (filters.budget) {
      filtered = filtered.filter(photographer => {
        const relevantPrices = getFilteredPricesForService(photographer, filters.services);
        
        if (relevantPrices.length === 0) return false;

        const minPrice = Math.min(...relevantPrices);
        const maxPrice = Math.max(...relevantPrices);

        if (filters.budget.includes('+')) {
          // Handle "above" ranges like "50000+"
          const threshold = parseInt(filters.budget.replace('+', ''));
          return minPrice >= threshold;
        } else if (filters.budget.includes('-')) {
          // Handle ranges like "25000-35000"
          const [min, max] = filters.budget.split('-').map(val => parseInt(val));
          return (minPrice >= min && minPrice <= max) || (maxPrice >= min && maxPrice <= max) || (minPrice <= min && maxPrice >= max);
        }
        
        return true;
      });
    }

    if (filters.rating) {
      filtered = filtered.filter(photographer =>
        photographer.rating >= parseInt(filters.rating)
      );
    }

    if (filters.location) {
      filtered = filtered.filter(photographer =>
        photographer.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    setFilteredPhotographers(filtered);
    setCurrentPage(1);
  }, [searchTerm, filters, allPhotographers]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      budget: '',
      services: '',
      rating: '',
      location: ''
    });
    setSearchTerm('');
  };

  // Enhanced star rendering for accurate ratings (supports decimal ratings)
  const renderStars = (rating) => {
    const numRating = parseFloat(rating) || 0;
    const fullStars = Math.floor(numRating);
    const hasHalfStar = (numRating % 1) >= 0.5;

    return Array.from({ length: 5 }, (_, index) => {
      const isFilled = index < fullStars || (index === fullStars && hasHalfStar);
      return (
        <svg
          key={index}
          className={`browse-star ${isFilled ? 'filled' : ''}`}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="m12 2 2.9 6.3 6.8.8-5 4.7 1.3 6.7-6-3.4-6 3.4 1.3-6.7-5-4.7 6.8-.8Z" />
        </svg>
      );
    });
  };

  // Navigate to photographer portfolio
  const handlePhotographerClick = (photographer) => {
    navigate(`/photographer/${photographer.id}`);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPhotographers = filteredPhotographers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPhotographers.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const loadMorePhotographers = () => {
    if (hasMore && !isLoading) {
      loadPhotographers(true);
    }
  };
   const [email, setEmail] = useState('');
  const handleSubscribe = async (e) => {
  e.preventDefault();
  
  if (!email || !/\S+@\S+\.\S+/.test(email)) {
    console.log('Please enter a valid email');
    return;
  }

  try {
    // Save to Firestore 'subscribers' collection
    await addDoc(collection(db, 'subscribers'), {
      email: email,
      subscribedAt: new Date(),
      source: 'landing_page_footer'
    });
    
    console.log('Successfully subscribed!');
    setEmail(''); // Clear input
    // Optional: toast.success('Thank you for subscribing!');
  } catch (error) {
    console.error('Subscription error:', error);
    // Optional: toast.error('Failed to subscribe. Please try again.');
  }
};

   const [showTerms, setShowTerms] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [showCookie, setShowCookie] = useState(false);

  // Updated navbar auth section - Profile widget positioned in navbar
  const renderNavbarAuthSection = () => {
    if (authLoading) {
      return (
        <div className="browse-auth-loading">
          <div className="browse-loading-spinner"></div>
        </div>
      );
    }

    if (logged_in && userProfile) {
      return (
        <div className="browse-navbar-profile-widget" ref={profileWidgetRef}>
          <div className="browse-navbar-profile-trigger" onClick={toggleProfileMenu}>
            <img
              src={userProfile.avatar}
              alt={userProfile.name}
              className="browse-navbar-profile-avatar"
              onError={(e) => {
                e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile.name)}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
              }}
            />
            <div className="browse-navbar-profile-info">
              <span className="browse-navbar-profile-name">{userProfile.name}</span>
              <span className="browse-navbar-profile-role">
                {userProfile.userType === 'photographer' || userProfile.userType === 'vendor' ? 'Photographer' : 'User'}
              </span>
            </div>
            <span className="browse-navbar-profile-chevron">▼</span>
          </div>

          {showProfileMenu && (
            <div className="browse-navbar-profile-menu">
              <div className="browse-navbar-profile-menu-header">
                <img
                  src={userProfile.avatar}
                  alt={userProfile.name}
                  className="browse-navbar-profile-menu-avatar"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile.name)}&background=6366f1&color=fff&size=128&bold=true&font-size=0.6`;
                  }}
                />
                <div className="browse-navbar-profile-menu-info">
                  <h4>{userProfile.name}</h4>
                  <p>{userProfile.email}</p>
                  {userProfile.isVerified && (
                    <span className="browse-navbar-profile-menu-badge">Verified</span>
                  )}
                </div>
              </div>

              <div className="browse-navbar-profile-menu-item browse-navbar-profile-menu-item-primary" onClick={handleProfileClick}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 7.5V9M15 10.5V19L13.5 17.5L12 19L10.5 17.5L9 19V10.5H15Z"/>
                </svg>
                {userProfile.userType === 'photographer' || userProfile.userType === 'vendor' ? 'Dashboard' : 'Profile Settings'}
              </div>

              {userProfile.userType !== 'photographer' && userProfile.userType !== 'vendor' && (
                <>
                  <div className="browse-navbar-profile-menu-item" onClick={handleMyBookingsClick}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 3H18V1H16V3H8V1H6V3H5C3.89 3 3.01 3.9 3.01 5L3 19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V8H19V19Z"/>
                    </svg>
                    My Bookings
                  </div>

                  <div className="browse-navbar-profile-menu-item" onClick={handleSettingsClick}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                    </svg>
                    Settings
                  </div>
                </>
              )}

              <div className="browse-navbar-profile-menu-divider"></div>

              <div className="browse-navbar-profile-menu-item browse-navbar-profile-menu-item-logout" onClick={handleLogout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16,17V14H9V10H16V7L21,12L16,17M14,2A2,2 0 0,1 16,4V6H14V4H5V20H14V18H16V20A2,2 0 0,1 14,22H5A2,2 0 0,1 3,20V4A2,2 0 0,1 5,2H14Z"/>
                </svg>
                Logout
              </div>
            </div>
          )}
        </div>
      );
    }



   
    return (
      <button className="browse-login-btn" onClick={() => navigate('/login')}>
        Login
      </button>
    );
  };

  const isPhotographer = userProfile?.userType === 'photographer' || userProfile?.userType === 'vendor';

  return (
    <>
    <div className="browse-container">
      {/* Single header — desktop links hide via CSS media query rather
          than a JS isMobile branch, which could render the wrong header
          when the initial window width was read before layout settled. */}
      <header className="browse-header">
          <div className="browse-top-left-logo" onClick={() => navigate('/LandingPage')}>
            <img src={logo} alt="LitStudio" className="browse-top-logo-image" />
          </div>

          <div className="browse-nav">
            <button className="browse-nav-btn" onClick={() => navigate('/LandingPage')}>Home</button>
            <button className="browse-nav-btn" onClick={() => navigate('/login')}>Join as Photographer</button>
            <button className="browse-nav-btn" onClick={() => navigate('/AboutUs')}>About Us</button>
          </div>

          <div className="browse-navbar-search">
            <input
              type="text"
              placeholder="Search photographers, locations, services..."
              className="browse-navbar-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <span className="browse-search-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
          </div>

        <div className="browse-nav-actions">
          {renderNavbarAuthSection()}
          <button
            type="button"
            className={`browse-hamburger ${isNavMenuOpen ? 'active' : ''}`}
            onClick={() => setIsNavMenuOpen(!isNavMenuOpen)}
            aria-label="Toggle navigation menu"
            aria-expanded={isNavMenuOpen}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </header>

      {/* Mobile nav — .browse-nav is display:none below 860px, so without
          this the Home/About links were unreachable on a phone. */}
      <div className={`browse-mobile-menu ${isNavMenuOpen ? 'active' : ''}`}>
        <button type="button" className="browse-mobile-nav-link" onClick={() => { setIsNavMenuOpen(false); navigate('/LandingPage'); }}>Home</button>
        <button type="button" className="browse-mobile-nav-link" onClick={() => { setIsNavMenuOpen(false); navigate('/login'); }}>Join as Photographer</button>
        <button type="button" className="browse-mobile-nav-link" onClick={() => { setIsNavMenuOpen(false); navigate('/AboutUs'); }}>About Us</button>
      </div>

      {/* Page intro */}
      <section className="browse-intro">
        <div className="browse-intro-inner">
          <h1>Find your photographer</h1>
          <p>
            Browse verified photographers, compare portfolios and pricing upfront,
            and book the one whose work fits your story.
          </p>
        </div>
      </section>

      {/* 🔴 UPDATED: Filter Section with Dynamic Options */}
      <div className="browse-filter-section">
        <div className="browse-filters">
          <select
            className={`browse-filter-select ${filters.budget ? 'is-active' : ''}`}
            value={filters.budget}
            onChange={(e) => handleFilterChange('budget', e.target.value)}
            aria-label="Filter by budget"
          >
            <option value="">All Budgets</option>
            {budgetRanges.map((range, index) => (
              <option key={index} value={range.value}>{range.label}</option>
            ))}
          </select>

          <select
            className={`browse-filter-select ${filters.services ? 'is-active' : ''}`}
            value={filters.services}
            onChange={(e) => handleFilterChange('services', e.target.value)}
            aria-label="Filter by service"
          >
            <option value="">All Services</option>
            {availableServices.map((service, index) => (
              <option key={index} value={service}>{service}</option>
            ))}
          </select>

          <select
            className={`browse-filter-select ${filters.rating ? 'is-active' : ''}`}
            value={filters.rating}
            onChange={(e) => handleFilterChange('rating', e.target.value)}
            aria-label="Filter by rating"
          >
            <option value="">All Ratings</option>
            <option value="4">4+ Stars</option>
            <option value="3">3+ Stars</option>
            <option value="2">2+ Stars</option>
          </select>

          <select
            className={`browse-filter-select ${filters.location ? 'is-active' : ''}`}
            value={filters.location}
            onChange={(e) => handleFilterChange('location', e.target.value)}
            aria-label="Filter by location"
          >
            <option value="">All Locations</option>
            {availableLocations.map((location, index) => (
              <option key={index} value={location}>{location}</option>
            ))}
          </select>

          {(filters.budget || filters.services || filters.rating || filters.location || searchTerm) && (
            <button className="browse-clear-btn" onClick={clearAllFilters}>
              Clear all
            </button>
          )}

          <button
            className="browse-mobile-filter-toggle"
            onClick={toggleMobileFilters}
            aria-label="Open filters"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="7" y1="12" x2="17" y2="12" />
              <line x1="10" y1="17" x2="14" y2="17" />
            </svg>
            Filters
          </button>

          {!isLoading && (
            <span className="browse-result-count">
              {filteredPhotographers.length}
              {filteredPhotographers.length === 1 ? ' photographer' : ' photographers'}
            </span>
          )}
        </div>
      </div>

      {/* Mobile Filter Overlay */}
      <div
        className={`browse-mobile-filter-overlay ${showMobileFilters ? 'open' : ''}`}
        onClick={closeMobileFilters}
      ></div>

      {/* 🔴 UPDATED: Mobile Filter Panel with Dynamic Options */}
      <div className={`browse-mobile-filter-panel ${showMobileFilters ? 'open' : ''}`}>
        <div className="browse-mobile-filter-header">
          <h3 className="browse-mobile-filter-title">Filter Photographers</h3>
          <button 
            className="browse-mobile-filter-close"
            onClick={closeMobileFilters}
            aria-label="Close filters"
          >
            ×
          </button>
        </div>
        
        <div className="browse-mobile-filter-content">
          <div className="browse-filters" style={{ flexDirection: 'column', gap: '1rem', padding: '0' }}>
            <select
              className="browse-filter-select"
              value={filters.budget}
              onChange={(e) => handleFilterChange('budget', e.target.value)}
            >
              <option value="">All Budgets</option>
              {budgetRanges.map((range, index) => (
                <option key={index} value={range.value}>{range.label}</option>
              ))}
            </select>

            <select
              className="browse-filter-select"
              value={filters.services}
              onChange={(e) => handleFilterChange('services', e.target.value)}
            >
              <option value="">All Services</option>
              {availableServices.map((service, index) => (
                <option key={index} value={service}>{service}</option>
              ))}
            </select>

            <select
              className="browse-filter-select"
              value={filters.rating}
              onChange={(e) => handleFilterChange('rating', e.target.value)}
            >
              <option value="">All Ratings</option>
              <option value="4">4+ Stars</option>
              <option value="3">3+ Stars</option>
              <option value="2">2+ Stars</option>
            </select>

            <select
              className="browse-filter-select"
              value={filters.location}
              onChange={(e) => handleFilterChange('location', e.target.value)}
            >
              <option value="">All Locations</option>
              {availableLocations.map((location, index) => (
                <option key={index} value={location}>{location}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="browse-mobile-filter-actions">
          <button 
            className="browse-mobile-filter-clear"
            onClick={handleMobileFilterClear}
          >
            Clear All
          </button>
          <button 
            className="browse-mobile-filter-apply"
            onClick={handleMobileFilterApply}
          >
            Apply Filters
          </button>
        </div>
      </div>

      {/* Loading — skeletons match the real card's shape so the grid
          does not reflow when data arrives */}
      {isLoading && allPhotographers.length === 0 && (
        <div className="browse-skeleton-grid" aria-busy="true" aria-label="Loading photographers">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="browse-skeleton-card" style={{ animationDelay: `${i * 70}ms` }}>
              <div className="browse-skeleton-media"></div>
              <div className="browse-skeleton-body">
                <div className="browse-skeleton-line w-70"></div>
                <div className="browse-skeleton-line w-45"></div>
                <div className="browse-skeleton-line w-90"></div>
                <div className="browse-skeleton-line w-35"></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No Results */}
      {!isLoading && filteredPhotographers.length === 0 && allPhotographers.length > 0 && (
        <div className="browse-no-results">
          <div className="browse-no-results-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>
          <h3>No photographers match those filters</h3>
          <p>Try widening your budget or clearing a filter to see more results.</p>
          <button type="button" className="browse-reset-btn" onClick={clearAllFilters}>
            Clear all filters
          </button>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && allPhotographers.length === 0 && (
        <div className="browse-empty-state">
          <div className="browse-empty-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
              <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
              <circle cx="12" cy="13" r="3.5" />
            </svg>
          </div>
          <h3>No photographers listed yet</h3>
          <p>Be the first photographer to join LitStudio and reach clients near you.</p>
          <button className="browse-join-btn" onClick={() => navigate('/Login')}>
            Join as Photographer
          </button>
        </div>
      )}

      {/* Cards Grid */}
      {filteredPhotographers.length > 0 && (
        <div className="browse-cards-container">
          {currentPhotographers.map((photographer) => (
            <div
              key={photographer.id}
              className="browse-card"
              onClick={() => handlePhotographerClick(photographer)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePhotographerClick(photographer);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="browse-card-media">
                {photographer.imageUrl ? (
                  <img
                    className="browse-card-image"
                    src={photographer.imageUrl}
                    alt={photographer.name}
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="browse-card-media-fallback">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3Z" />
                      <circle cx="12" cy="13" r="3.5" />
                    </svg>
                  </div>
                )}

                {(photographer.featured || photographer.isVerified) && (
                  <div className="browse-card-badges">
                    {photographer.featured && (
                      <span className="browse-card-badge">
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <path d="m12 2 2.9 6.3 6.8.8-5 4.7 1.3 6.7-6-3.4-6 3.4 1.3-6.7-5-4.7 6.8-.8Z" />
                        </svg>
                        Featured
                      </span>
                    )}
                    {photographer.isVerified && (
                      <span className="browse-verified-badge">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        Verified
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="browse-card-content">
                <h3 className="browse-studio-name">
                  <span>{photographer.name}</span>
                  {photographer.isVerified && (
                    <span className="browse-verified-icon" title="Verified photographer">
                      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M12 1.6 14.2 4l3.2-.3.9 3.1 2.8 1.6-1.3 3 1.3 3-2.8 1.6-.9 3.1-3.2-.3L12 22.4 9.8 20l-3.2.3-.9-3.1-2.8-1.6 1.3-3-1.3-3 2.8-1.6.9-3.1 3.2.3Z" />
                        <polyline points="8.5 12 11 14.5 15.5 9.5" fill="none" stroke="#fff" strokeWidth="2" />
                      </svg>
                    </span>
                  )}
                </h3>

                <div className="browse-location">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  <span>{photographer.location}</span>
                </div>

                {/* Rating renders only when real reviews exist — an unreviewed
                    photographer shows no score rather than an invented one. */}
                {photographer.reviewsCount > 0 && (
                  <div className="browse-rating">
                    <div className="browse-stars">
                      {renderStars(photographer.rating)}
                    </div>
                    <span className="browse-rating-text">
                      {photographer.rating.toFixed(1)} ({photographer.reviewsCount})
                    </span>
                  </div>
                )}

                <div className="browse-service-type">
                  <span className="browse-service-label">{photographer.services}</span>
                  {photographer.hasStudio && (
                    <span className="browse-studio-badge">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M3 21h18M5 21V7l7-4 7 4v14" />
                        <path d="M10 21v-5h4v5" />
                      </svg>
                      Studio
                    </span>
                  )}
                  {photographer.specialties.slice(0, 1).map((specialty, index) => (
                    <span key={index} className="browse-specialty-tag">{specialty}</span>
                  ))}
                </div>

                <div className="browse-price">
                  <span className="browse-price-label">From</span>
                  <span className="browse-price-amount">₹{photographer.price}</span>
                  {photographer.yearsExperience > 0 && (
                    <span className="browse-price-meta">{photographer.yearsExperience}+ yrs</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {filteredPhotographers.length > itemsPerPage && (
        <div className="browse-pagination">
          <button
            className="browse-pagination-btn"
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
          >
            ← Previous
          </button>

          {Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
            const pageNumber = currentPage <= 3 ? index + 1 : currentPage - 2 + index;
            if (pageNumber > totalPages) return null;

            return (
              <button
                key={pageNumber}
                className={`browse-pagination-btn ${currentPage === pageNumber ? 'active' : ''}`}
                onClick={() => paginate(pageNumber)}
              >
                {pageNumber}
              </button>
            );
          })}

          <button
            className="browse-pagination-btn"
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}

      {/* Load More Button */}
      {hasMore && allPhotographers.length > 0 && (
        <div className="browse-load-more">
          <button
            className="browse-load-more-btn"
            onClick={loadMorePhotographers}
            disabled={isLoading}
          >
            {isLoading ? 'Loading...' : 'Load More Photographers'}
          </button>
        </div>
      )}

      {/* Footer — must stay INSIDE .browse-container so the scoped footer
          rules apply. Outside it, the unscoped dark styling in
          PhotographerPortfolio.css wins and renders a navy footer. */}
      <footer className="landing-footer">
  <div className="landing-footer-content">
    <div className="landing-footer-text">
      <p>Whether it's your big day, a milestone, or just because - we bring the moment, we'll make it Lit!</p>
    </div>
    <div className="landing-subscription">
      <p>Subscribe and be first to get offers</p>
      <form className="landing-subscription-form" onSubmit={handleSubscribe}>
        <input 
          type="email" 
          placeholder="Enter your email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <button type="submit" className="landing-subscribe-btn">Subscribe!</button>
      </form>
    </div>
  </div>
  
  <div className="landing-social-links">
    <button type="button" className="landing-social-link" onClick={() => window.open('https://facebook.com', '_blank')}>
      <span>f</span>
    </button>
    <button type="button" className="landing-social-link" onClick={() => window.open('https://www.linkedin.com/company/thelitstudio/', '_blank')}>
      <span>in</span>
    </button>
    <button type="button" className="landing-social-link" onClick={() => window.open('https://www.instagram.com/litstudio.in/', '_blank')}>
      <span>ig</span>
    </button>
  </div>
  
  <div className="landing-footer-bottom">
    <p>&copy; 2025 LitStudio. All rights reserved.</p>
    <div className="landing-footer-links">
     <button type="button" onClick={() => setShowPrivacy(true)}>Privacy</button>

      <button type="button" onClick={() => setShowCookie(true)}>Cookie Settings</button>
      <button type="button" onClick={() => setShowTerms(true)}>Terms & Conditions</button>
    </div>
  </div>

  {/* Privacy Policy Modal */}
  <Modal 
    isOpen={showPrivacy} 
    onClose={() => setShowPrivacy(false)}
    title="Privacy Policy"
  >
    <p><strong>Effective Date:</strong> November 22, 2025</p>
    <p><strong>Last Updated:</strong> November 22, 2025</p>

    <h3>1. Introduction</h3>
    <p>
      Welcome to LitStudio ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our photography marketplace services.
    </p>

    <h3>2. Information We Collect</h3>
    
    <h4>2.1 Personal Information</h4>
    <p>We collect personal information that you voluntarily provide to us when you:</p>
    <ul>
      <li>Register on the platform as a user or photographer</li>
      <li>Make a booking or enquiry</li>
      <li>Subscribe to our newsletter</li>
      <li>Contact us for support</li>
    </ul>
    <p>This information may include:</p>
    <ul>
      <li>Full name and contact details (email address, phone number)</li>
      <li>Event details (type, date, location, requirements)</li>
      <li>Payment information (processed securely through third-party providers)</li>
      <li>Profile information and photographs (for photographers)</li>
      <li>User preferences and booking history</li>
    </ul>

    <h4>2.2 Automatically Collected Information</h4>
    <p>When you visit our website, we automatically collect certain information about your device, including:</p>
    <ul>
      <li>IP address and browser type</li>
      <li>Operating system and device information</li>
      <li>Usage data and browsing patterns</li>
      <li>Cookies and similar tracking technologies</li>
    </ul>

    <h3>3. How We Use Your Information</h3>
    <p>We use the information we collect to:</p>
    <ul>
      <li>Provide, operate, and maintain our photography marketplace services</li>
      <li>Process bookings and facilitate connections between clients and photographers</li>
      <li>Send you booking confirmations, updates, and customer support communications</li>
      <li>Improve our website and personalize your experience</li>
      <li>Send promotional emails and newsletters (with your consent)</li>
      <li>Detect, prevent, and address technical issues or fraudulent activity</li>
      <li>Comply with legal obligations and enforce our Terms & Conditions</li>
    </ul>

    <h3>4. Information Sharing and Disclosure</h3>
    <p>We do not sell your personal information. We may share your information with:</p>
    <ul>
      <li><strong>Photographers:</strong> When you make a booking, we share necessary details with the photographer</li>
      <li><strong>Service Providers:</strong> Third-party vendors who assist us (payment processors, email services, analytics)</li>
      <li><strong>Legal Compliance:</strong> When required by law or to protect our rights</li>
      <li><strong>Business Transfers:</strong> In connection with any merger, sale, or acquisition</li>
    </ul>

    <h3>5. Data Security</h3>
    <p>
      We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
    </p>

    <h3>6. Your Rights and Choices</h3>
    <p>You have the right to:</p>
    <ul>
      <li>Access, update, or delete your personal information</li>
      <li>Opt-out of marketing communications</li>
      <li>Disable cookies through your browser settings</li>
      <li>Request a copy of your data</li>
      <li>Withdraw consent for data processing</li>
    </ul>

    <h3>7. Data Retention</h3>
    <p>
      We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law.
    </p>

    <h3>8. Children's Privacy</h3>
    <p>
      Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
    </p>

    <h3>9. Third-Party Links</h3>
    <p>
      Our website may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
    </p>

    <h3>10. Changes to This Privacy Policy</h3>
    <p>
      We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
    </p>

    <h3>11. Contact Us</h3>
    <p>If you have questions about this Privacy Policy, please contact us:</p>
    <ul>
      <li><strong>Email:</strong>support@litstudio.in</li>
      <li><strong>Address:</strong> LitStudio, India</li>
    </ul>
  </Modal>

  {/* Cookie Policy Modal */}
  <Modal
    isOpen={showCookie}
    onClose={() => setShowCookie(false)}
    title="Cookie Policy"
  >
    <p><strong>Effective Date:</strong> November 22, 2025</p>
    <p><strong>Last Updated:</strong> November 22, 2025</p>

    <h3>1. What Are Cookies</h3>
    <p>
      Cookies are small text files stored on your device when you visit a website. They help the site remember information about your visit, such as your login session, so you don't have to re-enter it every time.
    </p>

    <h3>2. How We Use Cookies</h3>
    <p>
      LitStudio uses cookies for essential purposes only — primarily to keep you signed in and to remember your session while you browse or manage a booking. We do not currently use cookies for advertising, and we do not sell or share cookie data with third-party advertisers.
    </p>

    <h3>3. Types of Cookies We Use</h3>
    <ul>
      <li><strong>Strictly Necessary:</strong> Required for core functionality such as authentication and keeping you logged in. The site cannot function properly without these.</li>
      <li><strong>Preference Cookies:</strong> Remember choices you've made, such as your account type, so you don't have to set them again on each visit.</li>
    </ul>

    <h3>4. Managing Cookies</h3>
    <p>
      Most browsers let you view, manage, and delete cookies through their settings. Blocking strictly necessary cookies may prevent you from logging in or using core features of the platform.
    </p>

    <h3>5. Changes to This Policy</h3>
    <p>
      We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date.
    </p>

    <h3>6. Contact Us</h3>
    <p>If you have questions about this Cookie Policy, please contact us:</p>
    <ul>
      <li><strong>Email:</strong> support@litstudio.in</li>
      <li><strong>Address:</strong> LitStudio, India</li>
    </ul>
  </Modal>

  {/* Terms & Conditions Modal */}
  <Modal
  isOpen={showTerms}
  onClose={() => setShowTerms(false)}
  title="Terms & Conditions"
>
  <p><strong>Effective Date:</strong> November 22, 2025</p>
  <p><strong>Last Updated:</strong> November 22, 2025</p>

  <p>
    Welcome to LitStudio.in ("LitStudio", "we", "our", "us"). 
    LitStudio Technologies Private Limited operates this platform to connect Users with professional Photographers 
    for various types of shoots including weddings, events, personal portraits, food, fashion, and commercial photography.
  </p>
  <p>
    By using LitStudio, you ("User" or "Photographer") agree to comply with the following Terms & Conditions. 
    Please read them carefully before registering or making a booking.
  </p>

  <h3>1. Definitions</h3>
  <ul>
    <li><strong>Platform:</strong> The LitStudio website, mobile application, and associated services.</li>
    <li><strong>User/Client:</strong> Any individual or business using LitStudio to discover, compare, and book photographers.</li>
    <li><strong>Photographer:</strong> Any individual or business offering photography services through LitStudio.</li>
    <li><strong>Booking:</strong> A confirmed engagement between a User and a Photographer through LitStudio.</li>
    <li><strong>Content:</strong> All photographs, videos, reviews, text, logos, graphics, and other materials uploaded to the platform.</li>
    <li><strong>Intellectual Property (IP):</strong> Creative work such as photos, videos, and portfolios owned by the respective creators.</li>
  </ul>

  <h3>2. Acceptance of Terms</h3>
  <ul>
    <li>By registering or using LitStudio, you agree to these Terms, our Privacy Policy, and any future updates.</li>
    <li>You must be at least 18 years old and legally capable of entering into binding contracts.</li>
    <li>If you disagree with any part of these Terms, you must not use the platform.</li>
  </ul>

  <h3>3. Nature of Service</h3>
  <p><strong>LitStudio is a facilitator, not the service provider.</strong></p>
  <ul>
    <li>We provide a curated digital marketplace that helps Users connect with verified Photographers.</li>
    <li>All photography services are independently provided by photographers; LitStudio does not control the creative process or guarantee results.</li>
  </ul>

  <h3>4. User Terms</h3>
  
  <h4>4.1 Registration and Account</h4>
  <ul>
    <li>Users must create an account with valid contact details.</li>
    <li>Users are responsible for maintaining confidentiality of login credentials.</li>
    <li>Any suspicious activity must be immediately reported to <a href="mailto:support@litstudio.in">support@litstudio.in</a>.</li>
  </ul>

  <h4>4.2 Booking Process</h4>
  <ul>
    <li>Users can browse, select, and book photographers through the platform.</li>
    <li>Bookings are confirmed only upon successful payment through LitStudio's payment gateway.</li>
    <li>Users must provide accurate event details (date, location, duration, type, and preferences).</li>
  </ul>

  <h4>4.3 Payments and Fees</h4>
  <ul>
    <li>All payments are processed securely through LitStudio.</li>
    <li>A 1% convenience fee may apply to each booking.</li>
    <li>LitStudio holds the payment in escrow until the shoot is completed.</li>
    <li>Direct payments or offline transactions outside the platform are strictly prohibited.</li>
  </ul>

  <h4>4.4 Cancellations and Refunds</h4>
  <p><strong>Cancellation by User:</strong></p>
  <ul>
    <li>More than 7 days before the event → 80% refund</li>
    <li>Within 7 days → 50% refund</li>
    <li>Within 48 hours → No refund</li>
  </ul>
  <p><strong>Cancellation by Photographer:</strong></p>
  <ul>
    <li>Full 100% refund or replacement photographer will be provided.</li>
    <li>Refunds are processed within 7–10 business days.</li>
  </ul>

  <h4>4.5 User Conduct</h4>
  <ul>
    <li>Users must treat photographers with respect and professionalism.</li>
    <li>Harassment, abuse, or inappropriate behavior will lead to suspension or permanent ban.</li>
    <li>Users must not request or encourage photographers to bypass LitStudio for future bookings.</li>
  </ul>

  <h3>5. Photographer Terms</h3>
  
  <h4>5.1 Eligibility and Verification</h4>
  <ul>
    <li>Photographers must provide valid government-issued ID and proof of original portfolio work.</li>
    <li>All portfolios must feature the photographer's own authentic work — not copied, downloaded, or re-edited content from others.</li>
  </ul>

  <h4>5.2 Prohibition on Copying or Plagiarism</h4>
  <p>Photographers are <strong>strictly prohibited</strong> from:</p>
  <ul>
    <li>Uploading or claiming credit for others' photos or videos.</li>
    <li>Downloading, editing, or reposting work that belongs to another photographer.</li>
    <li>Using AI-generated or stock images without disclosure.</li>
  </ul>
  <p>If a photographer is found using copied or stolen work:</p>
  <ul>
    <li>The account will be permanently suspended.</li>
    <li>Payments may be withheld.</li>
    <li>Legal action may be initiated under the Copyright Act, 1957 (India).</li>
  </ul>

  <h4>5.3 Listings and Pricing</h4>
  <ul>
    <li>Photographers must accurately list services, packages, and sample work.</li>
    <li>Prices displayed must include all taxes and service charges.</li>
    <li>Once a booking is confirmed, the photographer must honor the agreed rate.</li>
  </ul>

  <h4>5.4 Membership and Commission</h4>
  <ul>
    <li>Photographers may be required to pay a monthly membership fee (₹70–₹100) to stay listed.</li>
    <li>LitStudio will deduct a 10% commission on each completed booking.</li>
  </ul>

  <h4>5.5 Delivery Commitments</h4>
  <ul>
    <li>Deliverables (edited photos/videos) must be submitted within the timeline agreed with the User.</li>
    <li>Delays beyond 7 days of the committed delivery date without valid cause may result in penalties or payment withholding.</li>
  </ul>

  <h4>5.6 Conduct and Professionalism</h4>
  <ul>
    <li>Photographers must maintain respectful behavior during shoots.</li>
    <li>Any misconduct (harassment, unprofessionalism, negligence) may result in immediate delisting.</li>
    <li>Communication with clients must remain through the LitStudio platform until the booking is confirmed.</li>
  </ul>

  <h3>6. Intellectual Property (IP) Rights</h3>
  
  <h4>6.1 Ownership</h4>
  <ul>
    <li>Photographers retain full ownership of their creative work.</li>
    <li>By uploading content, photographers grant LitStudio a non-exclusive, royalty-free license to use such content for marketing, promotional, and platform purposes.</li>
  </ul>

  <h4>6.2 Content Integrity</h4>
  <p>Users and photographers must not:</p>
  <ul>
    <li>Copy, reuse, or distribute others' photos/videos.</li>
    <li>Edit, watermark, or modify any portfolio images without permission.</li>
    <li>Claim ownership or remove author credits.</li>
  </ul>

  <h4>6.3 Copyright Violations</h4>
  <ul>
    <li>LitStudio maintains a zero-tolerance policy for plagiarism or IP theft.</li>
    <li>If reported, the accused photographer's account will be suspended immediately pending investigation.</li>
    <li>Repeat offenders will face permanent bans and legal proceedings.</li>
  </ul>

  <h3>7. Ratings, Reviews, and Feedback</h3>
  <ul>
    <li>After every booking, both User and Photographer may submit ratings and reviews.</li>
    <li>Reviews must be fair, honest, and based on personal experience.</li>
    <li>LitStudio may remove fake, offensive, or misleading reviews.</li>
    <li>Consistently high ratings may increase a photographer's visibility and tier level (Normal, Pro, Premium).</li>
  </ul>

  <h3>8. Dispute Resolution</h3>
  <ul>
    <li>Both parties should first attempt to resolve disputes amicably.</li>
    <li>If unresolved, LitStudio may mediate using booking details, chat history, and proof of delivery.</li>
    <li>LitStudio's mediation decision is final and binding for payment-related issues.</li>
  </ul>

  <h3>9. Prohibited Activities</h3>
  <p>Users or Photographers must not:</p>
  <ul>
    <li>Conduct transactions outside the LitStudio platform.</li>
    <li>Share false information, fake portfolios, or manipulated reviews.</li>
    <li>Upload offensive, obscene, or copyrighted material without permission.</li>
    <li>Attempt to hack, scrape, or misuse the platform.</li>
    <li>Engage in defamation, abuse, or discrimination against any user.</li>
  </ul>

  <h3>10. Liability and Disclaimer</h3>
  <ul>
    <li>LitStudio is not responsible for any disputes, damages, or losses arising between Users and Photographers.</li>
    <li>We do not guarantee the quality, quantity, or creative output of any service.</li>
    <li>LitStudio's total liability in any case shall not exceed the service fee earned by LitStudio from that booking.</li>
  </ul>

  <h3>11. Privacy</h3>
  <ul>
    <li>Personal data is collected, stored, and processed in accordance with our Privacy Policy.</li>
    <li>We do not sell or share personal data without consent, except when required by law.</li>
  </ul>

  <h3>12. Termination</h3>
  <ul>
    <li>LitStudio reserves the right to suspend or terminate any account for violations of these Terms.</li>
    <li>Users and Photographers may request account deletion by contacting <a href="mailto:support@litstudio.in">support@litstudio.in</a>.</li>
  </ul>

  <h3>13. Amendments</h3>
  <ul>
    <li>LitStudio may modify these Terms at any time.</li>
    <li>Updated terms will be published on this page, and continued use implies acceptance.</li>
  </ul>

  <h3>14. Governing Law</h3>
  <ul>
    <li>These Terms are governed by the laws of India.</li>
    <li>Jurisdiction for all disputes shall be the courts of Mumbai, Maharashtra.</li>
  </ul>

  <h3>15. Contact Us</h3>
  <p>For any queries, support, or grievance redressal:</p>
  <ul>
    <li><strong>Email:</strong> <a href="mailto:support@litstudio.in">support@litstudio.in</a></li>
    <li><strong>Website:</strong> <a href="https://www.litstudio.in">www.litstudio.in</a></li>
  </ul>

  <p style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f3f4f6', borderRadius: '8px', fontStyle: 'italic', color: '#6b7280' }}>
    By using LitStudio, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
  </p>
</Modal>
</footer>
    </div>
    </>
  );
  
};

export default BrowsePhotographer;
